import { useMutation, UseMutationOptions } from '@tanstack/react-query';

import { getRequest, postRequest, patchRequest, putRequest } from 'services/apiRequests';
import { StringifiedUUID } from 'types';
import {
  ActivityResponse,
  ArchiveThreadPayload,
  ArchiveThreadRequest,
  Comment,
  CreateCommentPayload,
  CreateCommentRequest,
  CreateThreadRequest,
  TQThread,
  UpdateProposedChangePayload,
  UpdateProposedChangeRequest,
} from 'types/threads';
import { APIError } from 'utils/errors';

const apiUrl = `/api/v1/collaboration/threads/`;
const tqCollaborationUrl = '/api/v1/tq/hierarchy-models/';

const createThread = (csrfToken: string) => (payload: CreateThreadRequest) => {
  return postRequest<CreateThreadRequest, TQThread>(apiUrl, payload, csrfToken);
};

const useCreateThreadMutation = (
  csrfToken: string,
  options: UseMutationOptions<TQThread, APIError, CreateThreadRequest> = {},
) => {
  return {
    mutation: useMutation<TQThread, APIError, CreateThreadRequest>({ ...options, mutationFn: createThread(csrfToken) }),
  };
};

const archiveThread = (csrfToken: string) => (payload: ArchiveThreadRequest) => {
  return patchRequest<ArchiveThreadPayload, null>(
    `${apiUrl}${payload.thread_id}/archive/`,
    { archive: true },
    csrfToken,
  );
};

const useArchiveThreadMutation = (
  csrfToken: string,
  options: UseMutationOptions<null, APIError, ArchiveThreadRequest> = {},
) => {
  return {
    mutation: useMutation<null, APIError, ArchiveThreadRequest>({ ...options, mutationFn: archiveThread(csrfToken) }),
  };
};

const updateProposedChange =
  (tqModelId: StringifiedUUID, csrfToken: string) => (payload: UpdateProposedChangeRequest) => {
    return putRequest<UpdateProposedChangePayload, null>(
      `/api/v1/tq/hierarchy-models/${tqModelId}/proposed-change-groups/${payload.proposed_change_id_group}/`,
      { accepted: payload.accepted, thread_id: payload.thread_id },
      csrfToken,
    );
  };

const useUpdateProposedChangeMutation = (
  tqModelId: StringifiedUUID,
  csrfToken: string,
  options: UseMutationOptions<null, APIError, UpdateProposedChangeRequest> = {},
) => {
  return {
    mutation: useMutation<null, APIError, UpdateProposedChangeRequest>({
      ...options,
      mutationFn: updateProposedChange(tqModelId, csrfToken),
    }),
  };
};

const createComment = (tqModelId: StringifiedUUID, csrfToken: string) => (request: CreateCommentRequest) => {
  return postRequest<CreateCommentPayload, Comment>(
    `${tqCollaborationUrl}${tqModelId}/threads/${request.thread_id}/comments/`,
    { content: request.content, metadata: request.metadata },
    csrfToken,
  );
};

const useCreateCommentMutation = (
  tqModelId: StringifiedUUID,
  csrfToken: string,
  options: UseMutationOptions<Comment, APIError, CreateCommentRequest, string> = {},
) => {
  return {
    mutation: useMutation<Comment, APIError, CreateCommentRequest, string>({
      ...options,
      mutationFn: createComment(tqModelId, csrfToken),
    }),
  };
};

const createSPMComment = (csrfToken: string) => (request: CreateCommentRequest) =>
  postRequest<CreateCommentPayload, Comment>(
    `${apiUrl}${request.thread_id}/comments/`,
    { content: request.content, metadata: request.metadata },
    csrfToken,
  );

const useCreateSPMCommentMutation = (
  csrfToken: string,
  options: UseMutationOptions<Comment, APIError, CreateCommentRequest> = {},
) => ({
  mutation: useMutation<Comment, APIError, CreateCommentRequest>({
    ...options,
    mutationFn: createSPMComment(csrfToken),
  }),
});

/**
 * Keys for the activity feed, which carries both comment threads and change logs. Kept here rather
 * than beside the query so a writer can invalidate it without importing the hook, which would close
 * a cycle back through ApprovalContext.
 */
export const spmActivityKeys = {
  base: () => ['spm-activity'] as const,
  feed: (planningCycleId: string, pcgUuid: string | undefined) =>
    [...spmActivityKeys.base(), planningCycleId, pcgUuid] as const,
};

const getActivity = (planningCycleId: string, pcgUuid: string): Promise<ActivityResponse> =>
  getRequest<null, ActivityResponse>(`/api/v1/tq/planning-cycles/${planningCycleId}/activity/${pcgUuid}/`);

/**
 * Picks the correct query to invalidate after a collaboration write. During an active approval
 * session (triggerPcgUuid set), threads are sourced from the activity query, not the legacy one
 * (see resolveVisibleTQThreads in collaboration-utils) — invalidating the legacy query in that
 * case is a no-op, since it's disabled for the duration of the session.
 */
const invalidateCollaboration = (
  triggerPcgUuid: string | undefined,
  invalidateActivity: () => void,
  invalidateGetThreadsQuery: () => void,
): void => {
  if (triggerPcgUuid) {
    invalidateActivity();
  } else {
    invalidateGetThreadsQuery();
  }
};

export const ThreadsService = {
  apiUrl,
  getActivity,
  useCreateThreadMutation,
  useCreateCommentMutation,
  useCreateSPMCommentMutation,
  useArchiveThreadMutation,
  useUpdateProposedChangeMutation,
  invalidateCollaboration,
};
